import React, { useEffect, useMemo, useState } from "react";
import Modal from 'react-bootstrap/Modal';
import { Alert, Button, Spinner } from 'react-bootstrap';
import * as _ from "lodash";
import './EditContractModal.scss';
import EditContractDetails from './EditContractDetails';
import ContractDetailsConfirmation from './ContractDetailsConfirmation';
import PhysicalLocation, { DetermineLaterLocation } from "domain/physicalLocation";
import * as ContractActions from "./contracts.actions";
import { DeliveryMethod } from "domain/deliveryMethod";
import DeliveryPeriod from "domain/deliveryPeriod";
import ValidationError from "common/validationError";
import notificationService from "common/notificationService";
import Permissions from "common/security/permissions";
import { listPaymentOptionsForContract } from "domain/paymentOption";
import FuturesContract from "domain/futuresContract";
import Contract, { getContractTypeName, ContractType } from "domain/contract";
import deliveryMethodService from "features/offers/deliveryMethodService";
import loggingService from "common/logging/loggingService";
import usePermission from "common/security/usePermission";
import { getTotalFees, isCashContractWithOpenStorage } from "./helpers";
import { FeeThin, getFeeThin } from "domain/fee";
import * as NP from "number-precision";
import { CustomField } from "domain/customField";
import AddCustomFieldModal from "features/customFields/AddCustomFieldModal";
import contractTypeHelper from "domain/contractTypeHelper";
import useDeliveryPeriods from "features/deliveryPeriods/useDeliveryPeriods";
import useFuturesContracts from "features/futuresContracts/useFuturesContracts";
import useTraders from "features/settings/traders/useTraders";
import useLocations from "features/locations/useLocations";
import useCustomFields from "features/merchants/useCustomFields";
import { localizeCommon, localizeOfferContract } from "features/localization/localizeService";
import { useDispatch } from "react-redux";
import useStoreSelector from "common/redux/useStoreSelector";
import useFees from "features/merchants/useFees";
import useJourney from "common/ui/useJourney";
import FormValidation, { useFormValidation } from "common/ui/FormValidation";
import useUiCustomization from "features/merchants/useUiCustomization";
import useEditableContractFields from "features/merchants/useEditableContractFields";
import useActiveMerchant from "features/merchants/useActiveMerchant";
import useContractExtendedTypes from "features/merchants/useContractExtendedTypes";
import { listAllowedFees } from "features/settings/general/fees/feeHelpers";
import { filterOutDeliveryMethodsInvalidForPeriod } from "features/offers/deliveryMethodHelper";

type EditContractProps = {
    contract: Contract;
    onClose(): void;
    onContractUpdated?: ((contract: Contract) => any);
};

enum ModalJourney {
    Details,
    CustomFields,
    Confirmation
}

const EditContractModal: React.FC<EditContractProps> = props => {

    const [isSubmitting, showSubmitting] = useState(false);
    const [contract, setContract] = useState<Contract>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [isPriceUpdated, setIsPriceUpdated] = useState(false);
    const [allowedDeliveryMethods, setAllowedDeliveryMethods] = useState<DeliveryMethod[]>([]);

    const futuresContracts = useFuturesContracts(props.contract.commodity.id);
    const deliveryPeriods = useDeliveryPeriods(props.contract.commodity.id);
    const customFields = useCustomFields();
    const fees = useFees();
    const editableFields = useEditableContractFields();
    const contractExtendedTypes = useContractExtendedTypes();

    const canPushBasis = usePermission(Permissions.PushBasis);
    const detailsValidator = useFormValidation();
    const customFieldsValidator = useFormValidation();

    const canEditContracts = usePermission(Permissions.EditContracts);
    const activeMerchant = useActiveMerchant();
    const isCustomer = useStoreSelector(m => m.userState.isCustomer);
    const myId = useStoreSelector(m => m.userState.internalId);

    const locations = useLocations();
    const traders = useTraders();
    const uiCustomization = useUiCustomization();
    const myTrader = traders.data.find(m => m.userId === myId);
    const canUseDetermineLater = myTrader?.locations.some(tl => tl.id === DetermineLaterLocation.id) ?? false;

    const dispatch = useDispatch();

    const journeySteps = useMemo(() => {
        const steps = [ModalJourney.Details, ModalJourney.Confirmation];

        if (!isCustomer && activeMerchant.useCustomFields && listCustomFields(contract).length > 0) {
            steps.splice(1, 0, ModalJourney.CustomFields);
        }

        return steps;
    }, [customFields, contract]);
    const journey = useJourney(journeySteps, handleSubmit);

    useEffect(() => {
        if (props.contract != null && deliveryPeriods != null) {
            const contract = { ...props.contract };
            contract.deliveryPeriod = contract.contractType === ContractType.BushelOnly
                ? contract.deliveryPeriod
                : listDeliveryPeriods(contract)?.find(m => m?.id === contract.deliveryPeriod?.id);

            if (activeMerchant.generalSettings.displayRegion) {
                if (contract.region == null) {
                    contract.region = contract.location == null ?
                        listLocations().find(l => l.homeLocation == null) :
                        contract.location;
                }
                else if (contract.location != null) {
                    // Since location object coming from Offers API just have id and name
                    contract.location.homeLocation = listLocations().find(m => m.id === contract.location?.id)?.homeLocation;
                }
            }

            contract.comments = null;

            setContract(contract);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.contract, canPushBasis])

    useEffect(() => {
        if (contract?.contractType == null) return;

        deliveryMethodService.list(contract.contractType)
            .then(setAllowedDeliveryMethods)
            .catch(err => {
                loggingService.error("Unable to retrieve delivery methods", err);
            });
    }, [contract?.contractType]);

    useEffect(() => {
        if (contract?.contractType == null || contract?.deliveryMethod == null) return;

        if (isCashContractWithOpenStorage(contract)) {
            setContract({ ...contract, finalPricingDate: contract.deliveryPeriodEndDate });
        }

    }, [contract?.contractType, contract?.deliveryMethod])

    function isLoaded(): boolean {
        return props.contract != null &&
            contract != null &&
            deliveryPeriods != null &&
            fees != null &&
            traders.isLoaded &&
            editableFields.isLoaded;
    }

    function goBack() {
        setErrors([]);
        journey.goBack();
    }

    function goForward() {
        let isValid = true;
        switch (journey.currentStep) {
            case ModalJourney.Details:
                isValid = detailsValidator.current.validate();
                break;
            case ModalJourney.CustomFields:
                isValid = customFieldsValidator.current.validate();
                break;
        }

        if (isValid) journey.goForward();
    }

    async function handleSubmit() {
        showSubmitting(true);
        try {
            await ContractActions.updateContract(contract)(dispatch);
            notificationService.success("Successfully updated contract!");
            props.onContractUpdated && props.onContractUpdated(contract);
            onClose();
        }
        catch (error) {
            showSubmitting(false);
            setErrors((error as ValidationError).errors ?? ["An unknown error occurred"]);
        }
    }

    function onClose() {
        props.onClose();
    }

    function listDeliveryPeriods(forContract: Contract): DeliveryPeriod[] {
        if (forContract == null) return [];

        let filteredDeliveryPeriods = [...deliveryPeriods];

        if (forContract.location != null && !contractTypeHelper.isHta(forContract.contractType)) {
            filteredDeliveryPeriods = deliveryPeriods.filter(dp => dp.bids?.some(b => (b.location.id === forContract.location.id) && b.cash != null));
        }

        if (filteredDeliveryPeriods?.length > 0) {
            if (forContract.deliveryPeriod != null && !filteredDeliveryPeriods.some(x => x.id === forContract.deliveryPeriod.id)) {
                filteredDeliveryPeriods.push(forContract.deliveryPeriod);
            }
        }
        else {
            return [forContract.deliveryPeriod];
        }

        return filteredDeliveryPeriods;
    }

    function listDeliveryMethods(forContract: Contract): DeliveryMethod[] {
        if (forContract == null) return [];

        return filterOutDeliveryMethodsInvalidForPeriod(allowedDeliveryMethods, forContract.deliveryMethod, forContract.deliveryPeriod, activeMerchant)
    }

    function listFuturesContracts(forContract: Contract): FuturesContract[] {

        if (forContract == null || forContract.commodity == null || forContract.commodity?.isCashOnly || listDeliveryPeriods(forContract).length === 0) return [];

        if (forContract.location == null || (canPushBasis && forContract.contractType === ContractType.Basis)) {
            return futuresContracts.data;
        }
        else if (canPushBasis) {
            const futureContracts = _.uniqBy(deliveryPeriods
                .filter(x => x.commodity.id === forContract.commodity.id
                    && x.contract != null
                    && (forContract.location == null || x.bids?.some(y => y.location.id === forContract.location?.id)))
                .map(x => x.contract), f => f.id);
            if (futureContracts?.length > 0) {
                if (forContract.futuresContract != null && !futureContracts?.some(x => x.id === forContract.futuresContract.id)) {
                    futureContracts.push(forContract.futuresContract);
                }
                return futureContracts;
            }
            else {
                return [forContract.deliveryPeriod?.contract ?? forContract.futuresContract];
            }
        }
        else {
            return [forContract.deliveryPeriod?.contract ?? forContract.futuresContract];
        }
    }

    function listLocations(): PhysicalLocation[] {
        if (myTrader != null && myTrader.locations.length !== locations.length) {
            return _.orderBy(locations.filter(l => myTrader.locations.some(tl => tl.id === l.id)), l => l.name);
        }

        return _.orderBy(locations, l => l.name);
    }

    function listFees(forContract: Contract): FeeThin[] {
        if (forContract == null) return [];

        let allowedFees = listAllowedFees(fees,
            contract.customerWillSell,
            contract.deliveryPeriod?.commodity ?? contract.commodity,
            contract.contractType)
            .map(f => getFeeThin(f));

        // Adding the fees coming from parent contract while setting futures or basis
        if (forContract.sourceContractId != null) {
            const missingParentFees = _.differenceBy(forContract.fees, allowedFees, m => m.id);
            allowedFees = [...allowedFees, ...missingParentFees];
        }

        return allowedFees;
    }

    function listCustomFields(forContract: Contract): CustomField[] {
        if (forContract == null || customFields == null || customFields.length === 0) return [];

        const allowedCustomFields = customFields
            .filter(f => {
                if (f.mappedField != null) return false;
                if (!f.allowedCommodities.includes(forContract.commodity.id)) return false;

                if (forContract.customerWillSell && !f.allowSale || !forContract.customerWillSell && !f.allowPurchase) return false;

                //Currently, offer is never null even if the contract doesn't have an offer. Check if the ID is 0 instead
                if (forContract.sourceContractId == null || forContract.offer.id === 0) {
                    if (!f.allowedContractTypes.includes(forContract.contractType)) return false;
                }
                else {
                    if (!f.allowedOfferTypes.includes(forContract.offer.type)) return false;
                }

                return true;

            });

        return allowedCustomFields;
    }

    function onContractUpdated(forContract: Contract): void {

        //Make sure all the other values are still valid.
        forContract.location = listLocations().find(m => m.id === forContract.location?.id);
        forContract.deliveryMethod = listDeliveryMethods(forContract).find(m => m === forContract.deliveryMethod);
        const deliveryPeriods = listDeliveryPeriods(forContract);
        forContract.deliveryPeriod = forContract.contractType === ContractType.BushelOnly
            ? forContract.deliveryPeriod
            : (deliveryPeriods.find(m => m?.id === forContract.deliveryPeriod?.id) ?? deliveryPeriods[0]);

        if (!(forContract.location == null || canPushBasis)) {
            forContract.futuresContract = forContract.deliveryPeriod?.contract;
        }

        if (forContract.cashPrice !== contract.cashPrice ||
            forContract.futuresPrice !== contract.futuresPrice) {
            setIsPriceUpdated(true);
        }

        forContract.finalPrice = getFinalPrice(forContract);

        setContract(forContract);
    }

    function getFinalPrice(forContract: Contract) {
        if (forContract == null) return 0;

        const price = contractTypeHelper.isHta(forContract.contractType) ? forContract.futuresPrice : forContract.cashPrice;
        const totalFees = getTotalFees(forContract);

        // price + premium - fees
        return NP.minus(NP.plus(price ?? 0, forContract.deliveryPeriod?.premium ?? 0), totalFees);
    }

    return !isLoaded()
        ? <></>
        : (
            <Modal show onHide={onClose} backdrop="static" >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {localizeOfferContract("EditContractModal_Header_EditContractNumber", { type: getContractTypeName(props.contract.contractType), contractId: props.contract.displayId })}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="add-contract-modal">
                    {props.contract.isSupervisorEditing && <Alert variant="warning">{localizeOfferContract("EditContractModal_Alert_SupervisorEdit")}</Alert>}
                    {journey.currentStep === ModalJourney.Details &&
                        <FormValidation ref={detailsValidator}>
                            <EditContractDetails
                                contract={contract}
                                canUseDetermineLater={canUseDetermineLater}
                                canPushBasis={canPushBasis}
                                locations={listLocations()}
                                contractExtendedTypes={contractExtendedTypes}
                                traders={traders.data}
                                deliveryMethods={listDeliveryMethods(contract)}
                                deliveryPeriods={listDeliveryPeriods(contract)}
                                futuresContracts={listFuturesContracts(contract)}
                                paymentOptions={listPaymentOptionsForContract(contract.contractType)}
                                onContractUpdated={onContractUpdated}
                                canEditContracts={canEditContracts}
                                isPriceUpdated={isPriceUpdated}
                                fees={listFees(contract)}
                                uiCustomization={uiCustomization}
                                editableFields={editableFields.data} />
                        </FormValidation>
                    }
                    {journey.currentStep === ModalJourney.CustomFields &&
                        <FormValidation ref={customFieldsValidator}>
                            <AddCustomFieldModal
                                customFields={listCustomFields(contract)}
                                selectedCustomFields={contract?.customFields ?? []}
                                onCustomFieldsChanged={cf => setContract({ ...contract, customFields: cf })}
                            />
                        </FormValidation>
                    }
                    {!isSubmitting && journey.currentStep === ModalJourney.Confirmation &&
                        <ContractDetailsConfirmation
                            contract={contract}
                            errors={errors}
                            uiCustomization={uiCustomization} />
                    }
                    {isSubmitting &&
                        <Spinner animation="border" />
                    }
                </Modal.Body>
                <Modal.Footer>
                    {journey.currentStep !== ModalJourney.Details &&
                        <Button variant="secondary" onClick={goBack} disabled={isSubmitting}
                            className={journey.currentStep === ModalJourney.Confirmation ? "mr-auto" : null}>
                            {localizeCommon("Button_GoBackAndEdit")}
                        </Button>
                    }
                    {[ModalJourney.Details, ModalJourney.Confirmation].includes(journey.currentStep) &&
                        <Button variant="secondary" onClick={props.onClose} disabled={isSubmitting}>
                            {localizeCommon("Button_Cancel")}
                        </Button>
                    }
                    <Button variant="primary" onClick={goForward} disabled={isSubmitting}>
                        {localizeCommon(journey.currentStep === ModalJourney.Confirmation
                            ? "Button_Submit"
                            : "Button_Next")}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
}

export default EditContractModal;